import React, {
    createContext,
    useContext,
    useState,
    useMemo,
    useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from './TenantContext';
import { AppRoutes } from '../Constants';
import {
    useGetTimePeriods,
    useGetEntitiesForTimePeriod,
} from '../api/generated/endpoint';
import type {
    TimePeriodResponse,
    EntityResponse,
} from '../api/generated/models';

interface EngagementContextValue {
    activeTimeId: number | undefined;
    selectedEntityId: number | undefined;
    setSelectedEntityId: (entityId: number | undefined) => void;
    timePeriods: TimePeriodResponse[] | undefined;
    entities: EntityResponse[] | undefined;
    isLoadingPeriods: boolean;
    isLoadingEntities: boolean;
}

const EngagementContext = createContext<EngagementContextValue | undefined>(
    undefined
);

interface EngagementProviderProps {
    children: React.ReactNode;
}

export const EngagementProvider: React.FC<EngagementProviderProps> = ({
    children,
}) => {
    const { selectedTenant } = useTenant();
    const location = useLocation();

    // Only fetch engagement data when inside an active engagement
    const isEngagementRoute =
        location.pathname !== AppRoutes.HomePage &&
        location.pathname !== AppRoutes.LandingPage &&
        location.pathname !== AppRoutes.Management;

    const { data: timePeriods, isLoading: isLoadingPeriods } =
        useGetTimePeriods({
            query: { enabled: isEngagementRoute },
        }) as { data: TimePeriodResponse[] | undefined; isLoading: boolean };

    const activeTimeId = useMemo(() => {
        return timePeriods?.find((p) => p.isActive)?.timeId;
    }, [timePeriods]);

    const { data: entities, isLoading: isLoadingEntities } =
        useGetEntitiesForTimePeriod(activeTimeId ?? 0, {
            query: { enabled: isEngagementRoute && !!activeTimeId },
        }) as { data: EntityResponse[] | undefined; isLoading: boolean };

    const [selectedEntityId, setSelectedEntityId] = useState<
        number | undefined
    >();

    // Auto-select entity when entities load, or clear stale selection on period/tenant change
    useEffect(() => {
        if (!entities) {
            setSelectedEntityId(undefined);
            return;
        }

        if (
            selectedEntityId !== undefined &&
            !entities.some((e) => e.entityId === selectedEntityId)
        ) {
            setSelectedEntityId(
                entities.length > 0 ? entities[0].entityId : undefined
            );
        } else if (selectedEntityId === undefined && entities.length > 0) {
            setSelectedEntityId(entities[0].entityId);
        }
    }, [entities, activeTimeId, selectedTenant]);

    const value = useMemo<EngagementContextValue>(
        () => ({
            activeTimeId,
            selectedEntityId,
            setSelectedEntityId,
            timePeriods,
            entities,
            isLoadingPeriods,
            isLoadingEntities,
        }),
        [
            activeTimeId,
            selectedEntityId,
            timePeriods,
            entities,
            isLoadingPeriods,
            isLoadingEntities,
        ]
    );

    return (
        <EngagementContext.Provider value={value}>
            {children}
        </EngagementContext.Provider>
    );
};

export const useEngagement = (): EngagementContextValue => {
    const context = useContext(EngagementContext);
    if (!context) {
        return {
            activeTimeId: undefined,
            selectedEntityId: undefined,
            setSelectedEntityId: () => undefined,
            timePeriods: undefined,
            entities: undefined,
            isLoadingPeriods: false,
            isLoadingEntities: false,
        };
    }
    return context;
};

export default EngagementContext;
